/**
 * BG-Bedarf — Summe aller Bedarfe einer Bedarfsgemeinschaft.
 *
 * Bedarf = Σ Regelbedarfe + Σ Mehrbedarfe + KdU
 *
 * KdU: § 22 SGB II — Kosten der Unterkunft und Heizung in tatsächlicher
 * Höhe, soweit angemessen. Die Angemessenheitsprüfung obliegt dem
 * Jobcenter; wir übernehmen die im Datenmodell hinterlegten KdU-Werte
 * 1:1.
 *
 * Pure Function — keine Seiteneffekte. Eingabe ist die `BgComposition`,
 * Ausgabe ein detailliertes Breakdown plus Summe.
 */
import type { MoneyCents } from '../schema/brands.js';
import type { BgComposition, BgMember } from '../schema/bgComposition.js';
import { REGELBEDARFE_2026_CENTS } from './regelbedarfe2026.js';
import { computeMehrbedarfPerMember, computeTotalMehrbedarfCents } from './computeMehrbedarf.js';

export interface BgBedarfBreakdown {
  /** Pro Mitglied: Regelbedarf + individueller Mehrbedarf */
  perMember: ReadonlyArray<{
    role: BgMember['role'];
    age: number;
    regelbedarfCents: MoneyCents;
    mehrbedarfCents: MoneyCents;
  }>;
  totalRegelbedarfCents: MoneyCents;
  totalMehrbedarfCents: MoneyCents;
  totalKduCents: MoneyCents;
  totalBedarfCents: MoneyCents;
}

function getMemberRegelbedarfCents(member: BgMember): MoneyCents {
  if (member.role === 'antragsteller') return REGELBEDARFE_2026_CENTS.RBS1;
  if (member.role === 'partner') return REGELBEDARFE_2026_CENTS.RBS2;
  // kind — Stufung nach Alter (Stichtag = aktuelles Datum, hier vereinfachend
  // die im Datenmodell hinterlegte Altersangabe):
  if (member.age <= 5) return REGELBEDARFE_2026_CENTS.RBS6;
  if (member.age <= 13) return REGELBEDARFE_2026_CENTS.RBS5;
  return REGELBEDARFE_2026_CENTS.RBS4;
}

export function computeBgBedarf(bg: BgComposition): BgBedarfBreakdown {
  const perMember = bg.members.map((m) => ({
    role: m.role,
    age: m.age,
    regelbedarfCents: getMemberRegelbedarfCents(m),
    mehrbedarfCents: computeMehrbedarfPerMember(m, bg),
  }));

  const totalRegelbedarfCents = perMember.reduce(
    (s, m) => s + (m.regelbedarfCents as number),
    0,
  ) as MoneyCents;

  const totalMehrbedarfCents = computeTotalMehrbedarfCents(bg);

  const totalKduCents = ((bg.kdu.kaltmiete as number) +
    (bg.kdu.nebenkosten as number) +
    (bg.kdu.heizkosten as number)) as MoneyCents;

  const totalBedarfCents = ((totalRegelbedarfCents as number) +
    (totalMehrbedarfCents as number) +
    (totalKduCents as number)) as MoneyCents;

  return {
    perMember,
    totalRegelbedarfCents,
    totalMehrbedarfCents,
    totalKduCents,
    totalBedarfCents,
  };
}
